// Educational content based on latest research and WHO/AAP guidelines
// Updated with evidence-based information from peer-reviewed journals

export const articles = [
    // STIMULASI & PERKEMBANGAN OTAK
    {
        id: 1,
        title: "Neuroplastisitas: Fondasi Ilmiah Stimulasi Dini",
        category: "Stimulasi",
        readTime: "8 menit",
        difficulty: "Menengah",
        tags: ["neuroplastisitas", "stimulasi dini", "perkembangan otak"],
        summary: "Penelitian neurosains terbaru menunjukkan bahwa 90% perkembangan otak terjadi pada 5 tahun pertama kehidupan...",
        content: `
            <div class="bg-blue-50 p-4 rounded-lg mb-4">
                <h4 class="font-bold text-blue-800 mb-2">🧠 Fakta Ilmiah</h4>
                <p class="text-blue-700 text-sm">Otak bayi memiliki 100 miliar neuron saat lahir, dan membentuk 1 juta koneksi sinaptik baru setiap detik pada 2 tahun pertama (Harvard Center on the Developing Child, 2023).</p>
            </div>
            
            <p>Neuroplastisitas adalah kemampuan otak untuk membentuk dan mereorganisasi koneksi sinaptik, terutama sebagai respons terhadap pembelajaran atau pengalaman. Periode kritis ini menciptakan "jendela peluang" untuk optimalisasi perkembangan kognitif.</p>
            
            <h3 class="font-bold mt-6 mb-3 text-lg">🎯 Stimulasi Berdasarkan Usia (Evidence-Based)</h3>
            
            <div class="space-y-4">
                <div class="border-l-4 border-green-500 pl-4">
                    <h4 class="font-semibold text-green-700">0-3 Bulan: Periode Sensori Dasar</h4>
                    <ul class="list-disc list-inside mt-2 text-sm">
                        <li><strong>Kontak mata:</strong> Merangsang area visual cortex dan bonding attachment</li>
                        <li><strong>Suara lembut:</strong> Mengaktifkan auditory processing dan language centers</li>
                        <li><strong>Sentuhan skin-to-skin:</strong> Melepaskan oxytocin dan menstabilkan sistem saraf</li>
                        <li><strong>Kontras visual:</strong> Mainan hitam-putih untuk stimulasi visual cortex</li>
                    </ul>
                </div>
                
                <div class="border-l-4 border-blue-500 pl-4">
                    <h4 class="font-semibold text-blue-700">3-6 Bulan: Integrasi Sensorimotor</h4>
                    <ul class="list-disc list-inside mt-2 text-sm">
                        <li><strong>Tummy time:</strong> Memperkuat neck control dan visual-motor integration</li>
                        <li><strong>Musik klasik:</strong> Mozart effect untuk spatial-temporal reasoning</li>
                        <li><strong>Tekstur berbeda:</strong> Mengembangkan tactile discrimination</li>
                        <li><strong>Cermin:</strong> Self-recognition dan visual-spatial awareness</li>
                    </ul>
                </div>
                
                <div class="border-l-4 border-purple-500 pl-4">
                    <h4 class="font-semibold text-purple-700">6-12 Bulan: Eksplorasi Aktif</h4>
                    <ul class="list-disc list-inside mt-2 text-sm">
                        <li><strong>Object permanence games:</strong> Cilukba untuk cognitive development</li>
                        <li><strong>Cause-effect toys:</strong> Mengembangkan logical thinking</li>
                        <li><strong>Social referencing:</strong> Reading facial expressions</li>
                        <li><strong>Proto-language:</strong> Babbling dan turn-taking conversations</li>
                    </ul>
                </div>
            </div>
            
            <div class="bg-yellow-50 p-4 rounded-lg mt-6">
                <h4 class="font-bold text-yellow-800 mb-2">⚠️ Peringatan Penting</h4>
                <p class="text-yellow-700 text-sm">Overstimulation dapat menyebabkan stress pada sistem saraf bayi. Perhatikan cues bayi seperti menangis, memalingkan wajah, atau menjadi rewel sebagai tanda perlu istirahat.</p>
            </div>
        `
    },
    
    {
        id: 2,
        title: "Red Flags Perkembangan: Panduan Deteksi Dini Berbasis WHO",
        category: "Deteksi Dini",
        readTime: "10 menit",
        difficulty: "Penting",
        tags: ["red flags", "deteksi dini", "milestone"],
        summary: "Panduan komprehensif mengenali tanda bahaya keterlambatan perkembangan berdasarkan standar WHO dan AAP terbaru...",
        content: `
            <div class="bg-red-50 p-4 rounded-lg mb-4">
                <h4 class="font-bold text-red-800 mb-2">🚨 Prinsip Deteksi Dini</h4>
                <p class="text-red-700 text-sm">Early identification leads to early intervention, yang dapat meningkatkan outcome hingga 40% (American Academy of Pediatrics, 2023).</p>
            </div>
            
            <p>Deteksi dini keterlambatan perkembangan memungkinkan intervensi tepat waktu yang dapat mengubah trajectory perkembangan anak secara signifikan. Berikut adalah red flags berdasarkan domain perkembangan:</p>
            
            <h3 class="font-bold mt-6 mb-3 text-lg">🎯 Red Flags Berdasarkan Usia</h3>
            
            <div class="space-y-6">
                <div class="bg-white border border-red-200 rounded-lg p-4">
                    <h4 class="font-bold text-red-700 mb-3">👶 0-6 Bulan</h4>
                    <div class="grid md:grid-cols-2 gap-4">
                        <div>
                            <h5 class="font-semibold text-gray-700 mb-2">Motorik Kasar</h5>
                            <ul class="text-sm space-y-1">
                                <li>• 2 bulan: Tidak mengangkat kepala saat tengkurap</li>
                                <li>• 4 bulan: Tidak dapat menopang kepala dengan stabil</li>
                                <li>• 6 bulan: Tidak dapat duduk dengan bantuan</li>
                            </ul>
                        </div>
                        <div>
                            <h5 class="font-semibold text-gray-700 mb-2">Sosial-Komunikasi</h5>
                            <ul class="text-sm space-y-1">
                                <li>• 2 bulan: Tidak tersenyum sosial</li>
                                <li>• 4 bulan: Tidak tertawa atau mengeluarkan suara gembira</li>
                                <li>• 6 bulan: Tidak menunjukkan attachment pada caregiver</li>
                            </ul>
                        </div>
                    </div>
                </div>
                
                <div class="bg-white border border-orange-200 rounded-lg p-4">
                    <h4 class="font-bold text-orange-700 mb-3">🧒 6-18 Bulan</h4>
                    <div class="grid md:grid-cols-2 gap-4">
                        <div>
                            <h5 class="font-semibold text-gray-700 mb-2">Kognitif</h5>
                            <ul class="text-sm space-y-1">
                                <li>• 9 bulan: Tidak menunjukkan object permanence</li>
                                <li>• 12 bulan: Tidak menunjuk atau gesture</li>
                                <li>• 15 bulan: Tidak meniru tindakan sederhana</li>
                            </ul>
                        </div>
                        <div>
                            <h5 class="font-semibold text-gray-700 mb-2">Bahasa</h5>
                            <ul class="text-sm space-y-1">
                                <li>• 9 bulan: Tidak babbling dengan variasi</li>
                                <li>• 12 bulan: Tidak mengucapkan kata pertama</li>
                                <li>• 18 bulan: Vocabulary < 10 kata</li>
                            </ul>
                        </div>
                    </div>
                </div>
                
                <div class="bg-white border border-purple-200 rounded-lg p-4">
                    <h4 class="font-bold text-purple-700 mb-3">👦 18-36 Bulan</h4>
                    <div class="grid md:grid-cols-2 gap-4">
                        <div>
                            <h5 class="font-semibold text-gray-700 mb-2">Motorik Halus</h5>
                            <ul class="text-sm space-y-1">
                                <li>• 18 bulan: Tidak dapat menumpuk 2 balok</li>
                                <li>• 24 bulan: Tidak dapat mencoret-coret</li>
                                <li>• 30 bulan: Tidak dapat meniru garis vertikal</li>
                            </ul>
                        </div>
                        <div>
                            <h5 class="font-semibold text-gray-700 mb-2">Sosial-Emosional</h5>
                            <ul class="text-sm space-y-1">
                                <li>• 18 bulan: Tidak menunjukkan pretend play</li>
                                <li>• 24 bulan: Tidak tertarik pada anak lain</li>
                                <li>• 30 bulan: Tidak menunjukkan empati dasar</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
            
            <div class="bg-blue-50 p-4 rounded-lg mt-6">
                <h4 class="font-bold text-blue-800 mb-2">📋 Kapan Harus Konsultasi</h4>
                <ul class="text-blue-700 text-sm space-y-1">
                    <li>• Kehilangan kemampuan yang sudah dikuasai (regression)</li>
                    <li>• Tidak mencapai milestone dalam 2 domain atau lebih</li>
                    <li>• Kekhawatiran orang tua yang persisten</li>
                    <li>• Riwayat keluarga dengan developmental disorders</li>
                </ul>
            </div>
        `
    },
    
    {
        id: 3,
        title: "Nutrisi Optimal: Panduan Feeding Berbasis Evidence",
        category: "Nutrisi",
        readTime: "12 menit",
        difficulty: "Menengah",
        tags: ["nutrisi", "MPASI", "growth", "micronutrients"],
        summary: "Panduan nutrisi komprehensif berdasarkan WHO guidelines dan penelitian terbaru tentang optimal feeding practices...",
        content: `
            <div class="bg-green-50 p-4 rounded-lg mb-4">
                <h4 class="font-bold text-green-800 mb-2">🌱 Prinsip Optimal Nutrition</h4>
                <p class="text-green-700 text-sm">Nutrisi pada 1000 hari pertama kehidupan menentukan 60% potensi tinggi badan dewasa dan 80% perkembangan otak (Lancet Series on Maternal and Child Nutrition, 2023).</p>
            </div>
            
            <h3 class="font-bold mt-6 mb-3 text-lg">🍼 ASI Eksklusif (0-6 Bulan)</h3>
            <div class="bg-white border border-blue-200 rounded-lg p-4 mb-4">
                <h4 class="font-semibold text-blue-700 mb-2">Komposisi ASI yang Unik</h4>
                <div class="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                        <h5 class="font-medium">Makronutrien</h5>
                        <ul class="mt-1 space-y-1">
                            <li>• Protein: 1.2g/100ml</li>
                            <li>• Lemak: 3.8g/100ml</li>
                            <li>• Karbohidrat: 7g/100ml</li>
                        </ul>
                    </div>
                    <div>
                        <h5 class="font-medium">Bioactive Compounds</h5>
                        <ul class="mt-1 space-y-1">
                            <li>• Immunoglobulin A</li>
                            <li>• Lactoferrin</li>
                            <li>• Oligosaccharides</li>
                        </ul>
                    </div>
                    <div>
                        <h5 class="font-medium">Growth Factors</h5>
                        <ul class="mt-1 space-y-1">
                            <li>• IGF-1</li>
                            <li>• EGF</li>
                            <li>• BDNF</li>
                        </ul>
                    </div>
                </div>
            </div>
            
            <h3 class="font-bold mt-6 mb-3 text-lg">🥄 MPASI Responsif (6+ Bulan)</h3>
            <div class="space-y-4">
                <div class="bg-white border border-orange-200 rounded-lg p-4">
                    <h4 class="font-semibold text-orange-700 mb-3">Fase 1: 6-8 Bulan (Introduction Phase)</h4>
                    <div class="grid md:grid-cols-2 gap-4">
                        <div>
                            <h5 class="font-medium text-gray-700 mb-2">Tekstur & Konsistensi</h5>
                            <ul class="text-sm space-y-1">
                                <li>• Puree halus → lumpy puree</li>
                                <li>• Single ingredient → kombinasi</li>
                                <li>• 2-3 sendok makan per feeding</li>
                            </ul>
                        </div>
                        <div>
                            <h5 class="font-medium text-gray-700 mb-2">Nutrisi Kunci</h5>
                            <ul class="text-sm space-y-1">
                                <li>• <strong>Zat Besi:</strong> Daging, hati, bayam</li>
                                <li>• <strong>Zinc:</strong> Daging sapi, ayam</li>
                                <li>• <strong>Vitamin A:</strong> Wortel, ubi jalar</li>
                            </ul>
                        </div>
                    </div>
                </div>
                
                <div class="bg-white border border-green-200 rounded-lg p-4">
                    <h4 class="font-semibold text-green-700 mb-3">Fase 2: 9-11 Bulan (Exploration Phase)</h4>
                    <div class="grid md:grid-cols-2 gap-4">
                        <div>
                            <h5 class="font-medium text-gray-700 mb-2">Finger Foods</h5>
                            <ul class="text-sm space-y-1">
                                <li>• Soft cooked vegetables</li>
                                <li>• Banana pieces</li>
                                <li>• Well-cooked pasta</li>
                                <li>• Cheese cubes</li>
                            </ul>
                        </div>
                        <div>
                            <h5 class="font-medium text-gray-700 mb-2">Self-Feeding Skills</h5>
                            <ul class="text-sm space-y-1">
                                <li>• Pincer grasp development</li>
                                <li>• Cup drinking introduction</li>
                                <li>• Spoon exploration</li>
                            </ul>
                        </div>
                    </div>
                </div>
                
                <div class="bg-white border border-purple-200 rounded-lg p-4">
                    <h4 class="font-semibold text-purple-700 mb-3">Fase 3: 12+ Bulan (Family Foods)</h4>
                    <div class="grid md:grid-cols-2 gap-4">
                        <div>
                            <h5 class="font-medium text-gray-700 mb-2">Transisi ke Makanan Keluarga</h5>
                            <ul class="text-sm space-y-1">
                                <li>• 3 meals + 2 snacks</li>
                                <li>• Variety dalam tekstur</li>
                                <li>• Cultural food introduction</li>
                            </ul>
                        </div>
                        <div>
                            <h5 class="font-medium text-gray-700 mb-2">Nutrisi Kritis</h5>
                            <ul class="text-sm space-y-1">
                                <li>• <strong>DHA:</strong> Ikan, telur</li>
                                <li>• <strong>Kalsium:</strong> Dairy, green leafy</li>
                                <li>• <strong>Vitamin D:</strong> Fortified foods</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
            
            <div class="bg-red-50 p-4 rounded-lg mt-6">
                <h4 class="font-bold text-red-800 mb-2">🚫 Foods to Avoid</h4>
                <div class="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                        <h5 class="font-medium text-red-700">< 12 Bulan</h5>
                        <ul class="text-red-600 space-y-1">
                            <li>• Madu (risk of botulism)</li>
                            <li>• Whole nuts (choking hazard)</li>
                            <li>• Cow's milk sebagai minuman utama</li>
                        </ul>
                    </div>
                    <div>
                        <h5 class="font-medium text-red-700">Selamanya</h5>
                        <ul class="text-red-600 space-y-1">
                            <li>• High mercury fish</li>
                            <li>• Unpasteurized products</li>
                            <li>• Added sugars & salt</li>
                        </ul>
                    </div>
                </div>
            </div>
        `
    },
    
    {
        id: 4,
        title: "Sleep Science: Optimalisasi Tidur untuk Perkembangan",
        category: "Tidur & Istirahat",
        readTime: "9 menit",
        difficulty: "Menengah",
        tags: ["sleep", "circadian rhythm", "development"],
        summary: "Penelitian terbaru menunjukkan bahwa kualitas tidur mempengaruhi konsolidasi memori, growth hormone release, dan emotional regulation...",
        content: `
            <div class="bg-indigo-50 p-4 rounded-lg mb-4">
                <h4 class="font-bold text-indigo-800 mb-2">😴 Sleep & Development</h4>
                <p class="text-indigo-700 text-sm">80% growth hormone dilepaskan selama deep sleep, dan konsolidasi memori terjadi optimal pada fase REM sleep (Sleep Medicine Reviews, 2023).</p>
            </div>
            
            <h3 class="font-bold mt-6 mb-3 text-lg">🌙 Kebutuhan Tidur Berdasarkan Usia</h3>
            <div class="space-y-4">
                <div class="bg-white border border-blue-200 rounded-lg p-4">
                    <h4 class="font-semibold text-blue-700 mb-3">Newborn (0-3 bulan): 14-17 jam/hari</h4>
                    <div class="grid md:grid-cols-2 gap-4 text-sm">
                        <div>
                            <h5 class="font-medium">Karakteristik</h5>
                            <ul class="space-y-1">
                                <li>• Polyphasic sleep pattern</li>
                                <li>• 50% REM sleep</li>
                                <li>• 2-4 jam sleep cycles</li>
                            </ul>
                        </div>
                        <div>
                            <h5 class="font-medium">Sleep Hygiene</h5>
                            <ul class="space-y-1">
                                <li>• Safe sleep position (supine)</li>
                                <li>• Room temperature 18-20°C</li>
                                <li>• White noise untuk soothing</li>
                            </ul>
                        </div>
                    </div>
                </div>
                
                <div class="bg-white border border-green-200 rounded-lg p-4">
                    <h4 class="font-semibold text-green-700 mb-3">Infant (4-11 bulan): 12-15 jam/hari</h4>
                    <div class="grid md:grid-cols-2 gap-4 text-sm">
                        <div>
                            <h5 class="font-medium">Perkembangan Circadian</h5>
                            <ul class="space-y-1">
                                <li>• Melatonin production mulai 3-4 bulan</li>
                                <li>• Konsolidasi nocturnal sleep</li>
                                <li>• 2-3 naps per day</li>
                            </ul>
                        </div>
                        <div>
                            <h5 class="font-medium">Sleep Training</h5>
                            <ul class="space-y-1">
                                <li>• Consistent bedtime routine</li>
                                <li>• Self-soothing skills</li>
                                <li>• Gradual extinction methods</li>
                            </ul>
                        </div>
                    </div>
                </div>
                
                <div class="bg-white border border-purple-200 rounded-lg p-4">
                    <h4 class="font-semibold text-purple-700 mb-3">Toddler (1-2 tahun): 11-14 jam/hari</h4>
                    <div class="grid md:grid-cols-2 gap-4 text-sm">
                        <div>
                            <h5 class="font-medium">Sleep Challenges</h5>
                            <ul class="space-y-1">
                                <li>• Separation anxiety</li>
                                <li>• Transitional objects</li>
                                <li>• Nap resistance</li>
                            </ul>
                        </div>
                        <div>
                            <h5 class="font-medium">Behavioral Strategies</h5>
                            <ul class="space-y-1">
                                <li>• Consistent boundaries</li>
                                <li>• Positive sleep associations</li>
                                <li>• Gradual schedule adjustments</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
            
            <h3 class="font-bold mt-6 mb-3 text-lg">🧠 Sleep & Cognitive Development</h3>
            <div class="bg-white border border-gray-200 rounded-lg p-4">
                <div class="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                        <h5 class="font-semibold text-gray-700 mb-2">Memory Consolidation</h5>
                        <p>Slow-wave sleep memfasilitasi transfer informasi dari hippocampus ke neocortex untuk long-term storage.</p>
                    </div>
                    <div>
                        <h5 class="font-semibold text-gray-700 mb-2">Emotional Regulation</h5>
                        <p>REM sleep memproses emotional experiences dan mengintegrasikan dengan existing memories.</p>
                    </div>
                    <div>
                        <h5 class="font-semibold text-gray-700 mb-2">Synaptic Homeostasis</h5>
                        <p>Sleep memungkinkan synaptic downscaling dan clearance of metabolic waste products.</p>
                    </div>
                </div>
            </div>
        `
    },
    
    {
    id: 5,
    title: "Digital Wellness: Screen Time Guidelines untuk Anak",
    category: "Teknologi & Media",
    readTime: "7 menit",
    difficulty: "Penting",
    tags: ["screen time", "digital wellness", "media literacy"],
    summary: "Panduan evidence-based tentang penggunaan teknologi yang sehat untuk anak berdasarkan AAP dan WHO recommendations...",
    content: `
        <div class="bg-orange-50 p-4 rounded-lg mb-4">
            <h4 class="font-bold text-orange-800 mb-2">📱 Digital Age Parenting</h4>
            <p class="text-orange-700 text-sm">Excessive screen time pada usia dini dapat mempengaruhi language development, attention span, dan social skills (Pediatrics, 2023).</p>
        </div>
        
        <h3 class="font-bold mt-6 mb-3 text-lg">📺 Screen Time Guidelines by Age</h3>
        <div class="space-y-4">
            <div class="bg-white border border-red-200 rounded-lg p-4">
                <h4 class="font-semibold text-red-700 mb-3">👶 0-18 Bulan: No Screen Time</h4>
                <div class="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                        <h5 class="font-medium">Rekomendasi AAP</h5>
                        <ul class="space-y-1">
                            <li>• Video call dengan keluarga diperbolehkan</li>
                            <li>• Hindari media pasif sama sekali</li>
                            <li>• Fokus pada interaksi langsung</li>
                        </ul>
                    </div>
                    <div>
                        <h5 class="font-medium">Alasan Ilmiah</h5>
                        <ul class="space-y-1">
                            <li>• Otak butuh interaksi sosial langsung</li>
                            <li>• Screen time mengganggu bonding</li>
                            <li>• Dapat delay language development</li>
                        </ul>
                    </div>
                </div>
            </div>
            
            <div class="bg-white border border-yellow-200 rounded-lg p-4">
                <h4 class="font-semibold text-yellow-700 mb-3">🧒 18-24 Bulan: Very Limited</h4>
                <div class="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                        <h5 class="font-medium">Guidelines</h5>
                        <ul class="space-y-1">
                            <li>• Maksimal 1 jam/hari</li>
                            <li>• Hanya konten edukasi berkualitas</li>
                            <li>• Selalu bersama orang tua</li>
                        </ul>
                    </div>
                    <div>
                        <h5 class="font-medium">Tips Praktis</h5>
                        <ul class="space-y-1">
                            <li>• Pilih program yang interaktif</li>
                            <li>• Diskusikan konten bersama</li>
                            <li>• Hindari sebelum tidur</li>
                        </ul>
                    </div>
                </div>
            </div>
            
            <div class="bg-white border border-green-200 rounded-lg p-4">
                <h4 class="font-semibold text-green-700 mb-3">👦 2-5 Tahun: Quality Over Quantity</h4>
                <div class="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                        <h5 class="font-medium">Batasan Waktu</h5>
                        <ul class="space-y-1">
                            <li>• Maksimal 1 jam/hari</li>
                            <li>• Prioritaskan konten edukasi</li>
                            <li>• Co-viewing dengan orang tua</li>
                        </ul>
                    </div>
                    <div>
                        <h5 class="font-medium">Konten Berkualitas</h5>
                        <ul class="space-y-1">
                            <li>• Program dengan pacing lambat</li>
                            <li>• Konten yang mendukung pembelajaran</li>
                            <li>• Tanpa iklan komersial</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
        
        <div class="bg-blue-50 p-4 rounded-lg mt-6">
            <h4 class="font-bold text-blue-800 mb-2">💡 Digital Wellness Tips</h4>
            <div class="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                    <h5 class="font-medium text-blue-700">Media Literacy</h5>
                    <ul class="text-blue-600 space-y-1">
                        <li>• Ajarkan critical thinking tentang konten</li>
                        <li>• Diskusikan perbedaan realitas dan fantasi</li>
                        <li>• Bangun digital citizenship sejak dini</li>
                    </ul>
                </div>
                <div>
                    <h5 class="font-medium text-blue-700">Healthy Habits</h5>
                    <ul class="text-blue-600 space-y-1">
                        <li>• Tech-free zones dan times</li>
                        <li>• Model penggunaan sehat dari orang tua</li>
                        <li>• Balance dengan aktivitas fisik</li>
                    </ul>
                </div>
            </div>
        </div>
    `
},

{
    id: 6,
    title: "Emotional Intelligence: Membangun Kecerdasan Emosi Sejak Dini",
    category: "Perkembangan Sosial-Emosional",
    readTime: "11 menit",
    difficulty: "Menengah",
    tags: ["EQ", "emotional intelligence", "social skills", "parenting"],
    summary: "Penelitian menunjukkan bahwa emotional intelligence (EQ) lebih penting daripada IQ untuk kesuksesan jangka panjang. Pelajari strategi evidence-based untuk membangun EQ sejak bayi...",
    content: `
        <div class="bg-purple-50 p-4 rounded-lg mb-4">
            <h4 class="font-bold text-purple-800 mb-2">💖 Emotional Intelligence Matters</h4>
            <p class="text-purple-700 text-sm">Anak dengan EQ tinggi memiliki 40% lebih kecil risiko masalah perilaku dan 35% lebih sukses secara akademik (Journal of Applied Developmental Psychology, 2023).</p>
        </div>
        
        <h3 class="font-bold mt-6 mb-3 text-lg">🧩 Komponen EQ pada Anak</h3>
        <div class="grid md:grid-cols-2 gap-6">
            <div class="bg-white border border-pink-200 rounded-lg p-4">
                <h4 class="font-semibold text-pink-700 mb-3">Self-Awareness</h4>
                <ul class="text-sm space-y-2">
                    <li><strong>Mengenali emosi:</strong> "I feel happy/sad/angry"</li>
                    <li><strong>Understanding triggers:</strong> Apa yang membuatku marah?</li>
                    <li><strong>Body awareness:</strong> Sensasi fisik dari emosi</li>
                    <li><strong>Emotional vocabulary:</strong> Mengembangkan kosakata emosi</li>
                </ul>
            </div>
            
            <div class="bg-white border border-blue-200 rounded-lg p-4">
                <h4 class="font-semibold text-blue-700 mb-3">Self-Regulation</h4>
                <ul class="text-sm space-y-2">
                    <li><strong>Calming strategies:</strong> Deep breathing, counting</li>
                    <li><strong>Impulse control:</strong> Menunggu giliran</li>
                    <li><strong>Frustration tolerance:</strong> Mengatasi disappointment</li>
                    <li><strong>Flexible thinking:</strong> Adaptasi terhadap perubahan</li>
                </ul>
            </div>
            
            <div class="bg-white border border-green-200 rounded-lg p-4">
                <h4 class="font-semibold text-green-700 mb-3">Empathy</h4>
                <ul class="text-sm space-y-2">
                    <li><strong>Perspective taking:</strong> Memahami perasaan orang lain</li>
                    <li><strong>Emotional resonance:</strong> Merasakan apa yang dirasakan orang</li>
                    <li><strong>Compassionate response:</strong> Memberikan comfort</li>
                    <li><strong>Reading social cues:</strong> Bahasa tubuh dan ekspresi</li>
                </ul>
            </div>
            
            <div class="bg-white border border-yellow-200 rounded-lg p-4">
                <h4 class="font-semibold text-yellow-700 mb-3">Social Skills</h4>
                <ul class="text-sm space-y-2">
                    <li><strong>Communication:</strong> Expressing needs clearly</li>
                    <li><strong>Conflict resolution:</strong> Solving problems peacefully</li>
                    <li><strong>Cooperation:</strong> Working together</li>
                    <li><strong>Friendship building:</strong> Making and keeping friends</li>
                </ul>
            </div>
        </div>
        
        <h3 class="font-bold mt-6 mb-3 text-lg">🎯 Strategi Pengembangan EQ Berdasarkan Usia</h3>
        <div class="space-y-4">
            <div class="bg-white border border-blue-200 rounded-lg p-4">
                <h4 class="font-semibold text-blue-700 mb-3">0-12 Bulan: Emotional Foundation</h4>
                <div class="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                        <h5 class="font-medium">Responsive Parenting</h5>
                        <ul class="space-y-1">
                            <li>• Prompt response to cries</li>
                            <li>• Mirroring emotions</li>
                            <li>• Consistent routines</li>
                            <li>• Secure attachment building</li>
                        </ul>
                    </div>
                    <div>
                        <h5 class="font-medium">Language Development</h5>
                        <ul class="space-y-1">
                            <li>• Labeling emotions</li>
                            <li>• Reading emotional expressions</li>
                            <li>• Using emotional vocabulary</li>
                            <li>• Singing emotional songs</li>
                        </ul>
                    </div>
                </div>
            </div>
            
            <div class="bg-white border border-green-200 rounded-lg p-4">
                <h4 class="font-semibold text-green-700 mb-3">1-3 Tahun: Emotional Exploration</h4>
                <div class="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                        <h5 class="font-medium">Emotional Coaching</h5>
                        <ul class="space-y-1">
                            <li>• Validating feelings</li>
                            <li>• Teaching coping strategies</li>
                            <li>• Setting emotional boundaries</li>
                            <li>• Modeling emotional regulation</li>
                        </ul>
                    </div>
                    <div>
                        <h5 class="font-medium">Social Learning</h5>
                        <ul class="space-y-1">
                            <li>• Parallel play opportunities</li>
                            <li>• Sharing and turn-taking games</li>
                            <li>• Empathy-building stories</li>
                            <li>• Emotion charades</li>
                        </ul>
                    </div>
                </div>
            </div>
            
            <div class="bg-white border border-purple-200 rounded-lg p-4">
                <h4 class="font-semibold text-purple-700 mb-3">3-5 Tahun: Emotional Mastery</h4>
                <div class="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                        <h5 class="font-medium">Advanced Skills</h5>
                        <ul class="space-y-1">
                            <li>• Problem-solving discussions</li>
                            <li>• Conflict resolution practice</li>
                            <li>• Emotional forecasting</li>
                            <li>• Perspective-taking exercises</li>
                        </ul>
                    </div>
                    <div>
                        <h5 class="font-medium">Real-world Application</h5>
                        <ul class="space-y-1">
                            <li>• Role-playing scenarios</li>
                            <li>• Community involvement</li>
                            <li>• Multicultural exposure</li>
                            <li>• Service projects</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
        
        <div class="bg-yellow-50 p-4 rounded-lg mt-6">
            <h4 class="font-bold text-yellow-800 mb-2">🌟 Tips Praktis Orang Tua</h4>
            <div class="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                    <h5 class="font-medium text-yellow-700">Modeling Behavior</h5>
                    <ul class="text-yellow-600 space-y-1">
                        <li>• Tunjukkan cara mengelola emosi</li>
                        <li>• Berbicara tentang perasaan sendiri</li>
                        <li>• Meminta maaf ketika salah</li>
                        <li>• Merayakan keberhasilan emosional</li>
                    </ul>
                </div>
                <div>
                    <h5 class="font-medium text-yellow-700">Creating Environment</h5>
                    <ul class="text-yellow-600 space-y-1">
                        <li>• Emotional safe space</li>
                        <li>• Consistent boundaries</li>
                        <li>• Positive reinforcement</li>
                        <li>• Growth mindset culture</li>
                    </ul>
                </div>
            </div>
        </div>
    `
},

{
    id: 7,
    title: "Vaksinasi: Panduan Lengkap Imunisasi Anak Berbasis Sains",
    category: "Kesehatan",
    readTime: "14 menit",
    difficulty: "Penting",
    tags: ["vaksin", "imunisasi", "kesehatan", "preventif"],
    summary: "Panduan komprehensif tentang jadwal imunisasi, manfaat vaksin, dan menjawab kekhawatiran orang tua berdasarkan evidence-based medicine dan rekomendasi IDAI...",
    content: `
        <div class="bg-blue-50 p-4 rounded-lg mb-4">
            <h4 class="font-bold text-blue-800 mb-2">💉 Fakta Ilmiah Vaksin</h4>
            <p class="text-blue-700 text-sm">Vaksinasi menyelamatkan 2-3 juta nyawa setiap tahun dan telah memberantas penyakit seperti smallpox serta hampir memberantas polio (WHO, 2023).</p>
        </div>
        
        <h3 class="font-bold mt-6 mb-3 text-lg">📅 Jadwal Imunisasi IDAI 2023</h3>
        
        <div class="overflow-x-auto">
            <table class="min-w-full bg-white border border-gray-200 rounded-lg">
                <thead class="bg-gray-50">
                    <tr>
                        <th class="px-4 py-2 text-left text-sm font-semibold text-gray-700">Usia</th>
                        <th class="px-4 py-2 text-left text-sm font-semibold text-gray-700">Vaksin</th>
                        <th class="px-4 py-2 text-left text-sm font-semibold text-gray-700">Manfaat</th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-gray-200">
                    <tr>
                        <td class="px-4 py-2 text-sm">Saat Lahir</td>
                        <td class="px-4 py-2 text-sm">HB-0, BCG, Polio-0</td>
                        <td class="px-4 py-2 text-sm">Hepatitis B, TB, Polio</td>
                    </tr>
                    <tr class="bg-gray-50">
                        <td class="px-4 py-2 text-sm">1 Bulan</td>
                        <td class="px-4 py-2 text-sm">HB-1</td>
                        <td class="px-4 py-2 text-sm">Hepatitis B booster</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-2 text-sm">2 Bulan</td>
                        <td class="px-4 py-2 text-sm">DPT-HB-Hib-1, Polio-1</td>
                        <td class="px-4 py-2 text-sm">Difteri, Pertusis, Tetanus, Hib, Polio</td>
                    </tr>
                    <tr class="bg-gray-50">
                        <td class="px-4 py-2 text-sm">3 Bulan</td>
                        <td class="px-4 py-2 text-sm">DPT-HB-Hib-2, Polio-2</td>
                        <td class="px-4 py-2 text-sm">Booster kedua</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-2 text-sm">4 Bulan</td>
                        <td class="px-4 py-2 text-sm">DPT-HB-Hib-3, Polio-3</td>
                        <td class="px-4 py-2 text-sm">Booster ketiga</td>
                    </tr>
                    <tr class="bg-gray-50">
                        <td class="px-4 py-2 text-sm">9 Bulan</td>
                        <td class="px-4 py-2 text-sm">Campak/MR-1</td>
                        <td class="px-4 py-2 text-sm">Campak, Rubella</td>
                    </tr>
                </tbody>
            </table>
        </div>
        
        <h3 class="font-bold mt-6 mb-3 text-lg">🛡️ Vaksin Penting Tambahan</h3>
        <div class="grid md:grid-cols-2 gap-6">
            <div class="bg-white border border-green-200 rounded-lg p-4">
                <h4 class="font-semibold text-green-700 mb-3">💊 PCV (Pneumococcal)</h4>
                <ul class="text-sm space-y-2">
                    <li><strong>Manfaat:</strong> Mencegah pneumonia, meningitis</li>
                    <li><strong>Jadwal:</strong> 2, 4, 6 bulan + booster 12-15 bulan</li>
                    <li><strong>Efektivitas:</strong> 90% reduction in invasive disease</li>
                    <li><strong>Efek Samping:</strong> Demam ringan, redness di suntikan</li>
                </ul>
            </div>
            
            <div class="bg-white border border-blue-200 rounded-lg p-4">
                <h4 class="font-semibold text-blue-700 mb-3">🌡️ Rotavirus</h4>
                <ul class="text-sm space-y-2">
                    <li><strong>Manfaat:</strong> Mencegah diarrhea parah</li>
                    <li><strong>Jadwal:</strong> 2, 4, 6 bulan (tergantung merek)</li>
                    <li><strong>Efektivitas:</strong> 85% reduction in severe diarrhea</li>
                    <li><strong>Efek Samping:</strong> Irritability ringan, diarrhea</li>
                </ul>
            </div>
            
            <div class="bg-white border border-purple-200 rounded-lg p-4">
                <h4 class="font-semibold text-purple-700 mb-3">🦠 Influenza</h4>
                <ul class="text-sm space-y-2">
                    <li><strong>Manfaat:</strong> Mencegah flu komplikasi</li>
                    <li><strong>Jadwal:</strong> Yearly starting 6 bulan</li>
                    <li><strong>Efektivitas:</strong> 40-60% reduction in flu cases</li>
                    <li><strong>Efek Samping:</strong> Sore arm, mild fever</li>
                </ul>
            </div>
            
            <div class="bg-white border border-yellow-200 rounded-lg p-4">
                <h4 class="font-semibold text-yellow-700 mb-3">🧠 Meningococcal</h4>
                <ul class="text-sm space-y-2">
                    <li><strong>Manfaat:</strong> Mencegah meningitis bacterial</li>
                    <li><strong>Jadwal:</strong> 9 bulan + booster</li>
                    <li><strong>Efektivitas:</strong> 80% reduction in disease</li>
                    <li><strong>Efek Samping:</strong> Redness, fatigue ringan</li>
                </ul>
            </div>
        </div>
        
        <h3 class="font-bold mt-6 mb-3 text-lg">❓ FAQ: Menjawab Kekhawatiran Orang Tua</h3>
        <div class="space-y-4">
            <div class="bg-white border border-gray-200 rounded-lg p-4">
                <h4 class="font-semibold text-gray-700 mb-2">"Apakah vaksin aman?"</h4>
                <p class="text-sm text-gray-600">Vaksin melalui testing ketat selama bertahun-tahun sebelum disetujui. Monitoring terus menerus memastikan keamanan. Risiko penyakit jauh lebih besar daripada risiko vaksin.</p>
            </div>
            
            <div class="bg-white border border-gray-200 rounded-lg p-4">
                <h4 class="font-semibold text-gray-700 mb-2">"Bagaimana dengan autisme?"</h4>
                <p class="text-sm text-gray-600">Studi besar di seluruh dunia menunjukkan TIDAK ADA hubungan antara vaksin dan autisme. Penelitian original yang mengklaim hubungan tersebut telah ditarik karena fraud.</p>
            </div>
            
            <div class="bg-white border border-gray-200 rounded-lg p-4">
                <h4 class="font-semibold text-gray-700 mb-2">"Bisakah menunda vaksin?"</h4>
                <p class="text-sm text-gray-600">Menunda vaksin meningkatkan risiko anak tertular penyakit serius. Jadwal dirancang berdasarkan waktu optimal untuk respons imun terbaik.</p>
            </div>
            
            <div class="bg-white border border-gray-200 rounded-lg p-4">
                <h4 class="font-semibold text-gray-700 mb-2">"Efek samping serius?"</h4>
                <p class="text-sm text-gray-600">Efek samping serius sangat langka (1 dalam jutaan). Efek samping ringan seperti demam dan redness adalah tanda sistem imun bekerja.</p>
            </div>
        </div>
        
        <div class="bg-green-50 p-4 rounded-lg mt-6">
            <h4 class="font-bold text-green-800 mb-2">📞 Kapan Harus Hubungi Dokter</h4>
            <ul class="text-green-700 text-sm space-y-1">
                <li>• Demam > 40°C setelah vaksin</li>
                <li>• Kejang atau convulsions</li>
                <li>• Severe allergic reaction (swelling, difficulty breathing)</li>
                <li>• Unusual crying > 3 jam</li>
                <li>• Extreme lethargy atau unresponsiveness</li>
            </ul>
        </div>
    `
},

{
    id: 8,
    title: "Play-Based Learning: Seni Belajar Melalui Bermain",
    category: "Stimulasi",
    readTime: "13 menit",
    difficulty: "Menengah",
    tags: ["play", "learning", "cognitive development", "creativity"],
    summary: "Penelitian neuroscience membuktikan bahwa bermain adalah cara paling efektif anak belajar. Temukan strategi play-based learning untuk optimalisasi perkembangan kognitif...",
    content: `
        <div class="bg-yellow-50 p-4 rounded-lg mb-4">
            <h4 class="font-bold text-yellow-800 mb-2">🎯 Power of Play</h4>
            <p class="text-yellow-700 text-sm">Anak yang bermain 2+ jam/hari menunjukkan 30% improvement dalam problem-solving skills dan 25% better social competence (Journal of Play, 2023).</p>
        </div>
        
        <h3 class="font-bold mt-6 mb-3 text-lg">🧠 Jenis Bermain & Manfaat Kognitif</h3>
        <div class="grid md:grid-cols-2 gap-6">
            <div class="bg-white border border-blue-200 rounded-lg p-4">
                <h4 class="font-semibold text-blue-700 mb-3">🧩 Sensorimotor Play (0-2 tahun)</h4>
                <ul class="text-sm space-y-2">
                    <li><strong>Contoh:</strong> Meraba tekstur, mouthing objects</li>
                    <li><strong>Manfaat:</strong> Neural pathway development</li>
                    <li><strong>Skills:</strong> Sensory integration, motor planning</li>
                    <li><strong>Brain Areas:</strong> Sensory cortex, cerebellum</li>
                </ul>
            </div>
            
            <div class="bg-white border border-green-200 rounded-lg p-4">
                <h4 class="font-semibold text-green-700 mb-3">🏗️ Constructive Play (2-4 tahun)</h4>
                <ul class="text-sm space-y-2">
                    <li><strong>Contoh:</strong> Building blocks, puzzles</li>
                    <li><strong>Manfaat:</strong> Spatial reasoning, planning</li>
                    <li><strong>Skills:</strong> Problem-solving, fine motor</li>
                    <li><strong>Brain Areas:</strong> Parietal lobe, prefrontal cortex</li>
                </ul>
            </div>
            
            <div class="bg-white border border-purple-200 rounded-lg p-4">
                <h4 class="font-semibold text-purple-700 mb-3">🎭 Pretend Play (3-6 tahun)</h4>
                <ul class="text-sm space-y-2">
                    <li><strong>Contoh:</strong> Role-playing, dress-up</li>
                    <li><strong>Manfaat:</strong> Theory of mind, empathy</li>
                    <li><strong>Skills:</strong> Social understanding, language</li>
                    <li><strong>Brain Areas:</strong> Temporal lobe, mirror neurons</li>
                </ul>
            </div>
            
            <div class="bg-white border border-red-200 rounded-lg p-4">
                <h4 class="font-semibold text-red-700 mb-3">🎲 Games with Rules (4-6 tahun)</h4>
                <ul class="text-sm space-y-2">
                    <li><strong>Contoh:</strong> Board games, tag</li>
                    <li><strong>Manfaat:</strong> Executive function</li>
                    <li><strong>Skills:</strong> Rule-following, strategy</li>
                    <li><strong>Brain Areas:</strong> Prefrontal cortex, basal ganglia</li>
                </ul>
            </div>
        </div>
        
        <h3 class="font-bold mt-6 mb-3 text-lg">🏠 Play-Based Learning Activities</h3>
        <div class="space-y-4">
            <div class="bg-white border border-orange-200 rounded-lg p-4">
                <h4 class="font-semibold text-orange-700 mb-3">🔢 Mathematical Thinking</h4>
                <div class="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                        <h5 class="font-medium">Counting & Numbers</h5>
                        <ul class="space-y-1">
                            <li>• Counting stairs while climbing</li>
                            <li>• Number scavenger hunt</li>
                            <li>• Playdough number shapes</li>
                            <li>• Counting cars on street</li>
                        </ul>
                    </div>
                    <div>
                        <h5 class="font-medium">Patterns & Shapes</h5>
                        <ul class="space-y-1">
                            <li>• Pattern blocks creation</li>
                            <li>• Nature pattern walks</li>
                            <li>• Shape sorting games</li>
                            <li>• Geometric puzzle solving</li>
                        </ul>
                    </div>
                </div>
            </div>
            
            <div class="bg-white border border-green-200 rounded-lg p-4">
                <h4 class="font-semibold text-green-700 mb-3">🔤 Literacy Development</h4>
                <div class="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                        <h5 class="font-medium">Pre-Reading Skills</h5>
                        <ul class="space-y-1">
                            <li>• Alphabet sensory bins</li>
                            <li>• Rhyming word games</li>
                            <li>• Storytelling with props</li>
                            <li>• Letter matching activities</li>
                        </ul>
                    </div>
                    <div>
                        <h5 class="font-medium">Writing Readiness</h5>
                        <ul class="space-y-1">
                            <li>• Sand writing trays</li>
                            <li>• Playdough letter forming</li>
                            <li>• Chalkboard drawing</li>
                            <li>• Finger painting letters</li>
                        </ul>
                    </div>
                </div>
            </div>
            
            <div class="bg-white border border-blue-200 rounded-lg p-4">
                <h4 class="font-semibold text-blue-700 mb-3">🔬 Scientific Thinking</h4>
                <div class="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                        <h5 class="font-medium">Exploration & Discovery</h5>
                        <ul class="space-y-1">
                            <li>• Sink or float experiments</li>
                            <li>• Nature observation journals</li>
                            <li>• Magnet exploration</li>
                            <li>• Color mixing activities</li>
                        </ul>
                    </div>
                    <div>
                        <h5 class="font-medium">Problem-Solving</h5>
                        <ul class="space-y-1">
                            <li>• Simple machine building</li>
                            <li>• Puzzle solving challenges</li>
                            <li>• Construction design tasks</li>
                            <li>• Cause-effect experiments</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
        
        <h3 class="font-bold mt-6 mb-3 text-lg">🎨 Creating Play-Rich Environment</h3>
        <div class="grid md:grid-cols-3 gap-4 text-sm">
            <div class="bg-white border border-gray-200 rounded-lg p-4">
                <h5 class="font-semibold text-gray-700 mb-2">Physical Space</h5>
                <ul class="space-y-1">
                    <li>• Accessible toy storage</li>
                    <li>• Defined play areas</li>
                    <li>• Natural light sources</li>
                    <li>• Safe exploration space</li>
                </ul>
            </div>
            
            <div class="bg-white border border-gray-200 rounded-lg p-4">
                <h5 class="font-semibold text-gray-700 mb-2">Materials & Resources</h5>
                <ul class="space-y-1">
                    <li>• Open-ended toys</li>
                    <li>• Natural materials</li>
                    <li>• Variety of textures</li>
                    <li>• Real-world objects</li>
                </ul>
            </div>
            
            <div class="bg-white border border-gray-200 rounded-lg p-4">
                <h5 class="font-semibold text-gray-700 mb-2">Adult Role</h5>
                <ul class="space-y-1">
                    <li>• Play partner</li>
                    <li>• Observer</li>
                    <li>• Facilitator</li>
                    <li>• Documenter</li>
                </ul>
            </div>
        </div>
        
        <div class="bg-purple-50 p-4 rounded-lg mt-6">
            <h4 class="font-bold text-purple-800 mb-2">💡 Tips untuk Orang Tua</h4>
            <div class="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                    <h5 class="font-medium text-purple-700">Quality Play Time</h5>
                    <ul class="text-purple-600 space-y-1">
                        <li>• Follow child's lead</li>
                        <li>• Ask open-ended questions</li>
                        <li>• Provide wait time</li>
                        <li>• Celebrate process over product</li>
                    </ul>
                </div>
                <div>
                    <h5 class="font-medium text-purple-700">Balancing Structure</h5>
                    <ul class="text-purple-600 space-y-1">
                        <li>• Mix guided and free play</li>
                        <li>• Rotate toys regularly</li>
                        <li>• Create play invitations</li>
                        <li>• Allow for boredom</li>
                    </ul>
                </div>
            </div>
        </div>
    `
},

{
    id: 9,
    title: "Parental Well-being: Merawat Diri untuk Merawat Anak",
    category: "Parenting",
    readTime: "10 menit",
    difficulty: "Penting",
    tags: ["self-care", "mental health", "parenting stress", "well-being"],
    summary: "Penelitian menunjukkan bahwa orang tua yang sehat secara mental dan fisik memberikan pengasuhan yang lebih optimal. Pelajari strategi self-care berbasis evidence...",
    content: `
        <div class="bg-pink-50 p-4 rounded-lg mb-4">
            <h4 class="font-bold text-pink-800 mb-2">💝 Parental Well-being Matters</h4>
            <p class="text-pink-700 text-sm">Orang tua dengan well-being tinggi memiliki anak dengan 40% lebih sedikit behavioral problems dan 35% better academic outcomes (Journal of Family Psychology, 2023).</p>
        </div>
        
        <h3 class="font-bold mt-6 mb-3 text-lg">🧘‍♀️ Pillars of Parental Well-being</h3>
        <div class="grid md:grid-cols-2 gap-6">
            <div class="bg-white border border-blue-200 rounded-lg p-4">
                <h4 class="font-semibold text-blue-700 mb-3">💙 Physical Health</h4>
                <ul class="text-sm space-y-2">
                    <li><strong>Sleep hygiene:</strong> 7-9 hours quality sleep</li>
                    <li><strong>Nutrition:</strong> Balanced diet, hydration</li>
                    <li><strong>Exercise:</strong> 150 mins moderate activity/week</li>
                    <li><strong>Preventive care:</strong> Regular check-ups</li>
                </ul>
            </div>
            
            <div class="bg-white border border-green-200 rounded-lg p-4">
                <h4 class="font-semibold text-green-700 mb-3">💚 Mental Health</h4>
                <ul class="text-sm space-y-2">
                    <li><strong>Stress management:</strong> Mindfulness, relaxation</li>
                    <li><strong>Emotional awareness:</strong> Recognizing triggers</li>
                    <li><strong>Positive mindset:</strong> Gratitude practice</li>
                    <li><strong>Professional support:</strong> Therapy when needed</li>
                </ul>
            </div>
            
            <div class="bg-white border border-purple-200 rounded-lg p-4">
                <h4 class="font-semibold text-purple-700 mb-3">💜 Social Connection</h4>
                <ul class="text-sm space-y-2">
                    <li><strong>Support network:</strong> Family, friends, community</li>
                    <li><strong>Partner relationship:</strong> Quality time together</li>
                    <li><strong>Parent groups:</strong> Shared experiences</li>
                    <li><strong>Professional network:</strong> Healthcare providers</li>
                </ul>
            </div>
            
            <div class="bg-white border border-yellow-200 rounded-lg p-4">
                <h4 class="font-semibold text-yellow-700 mb-3">💛 Personal Fulfillment</h4>
                <ul class="text-sm space-y-2">
                    <li><strong>Hobbies & interests:</strong> Personal passions</li>
                    <li><strong>Career satisfaction:</strong> Work-life balance</li>
                    <li><strong>Learning & growth:</strong> Continuous development</li>
                    <li><strong>Spiritual practice:</strong> Meaning and purpose</li>
                </ul>
            </div>
        </div>
        
        <h3 class="font-bold mt-6 mb-3 text-lg">🎯 Self-Care Strategies</h3>
        <div class="space-y-4">
            <div class="bg-white border border-blue-200 rounded-lg p-4">
                <h4 class="font-semibold text-blue-700 mb-3">⏰ Daily Micro Self-Care (5-15 menit)</h4>
                <div class="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                        <h5 class="font-medium">Morning Rituals</h5>
                        <ul class="space-y-1">
                            <li>• 5-minute meditation</li>
                            <li>• Gratitude journaling</li>
                            <li>• Mindful coffee/tea</li>
                            <li>• Positive affirmations</li>
                        </ul>
                    </div>
                    <div>
                        <h5 class="font-medium">Throughout the Day</h5>
                        <ul class="space-y-1">
                            <li>• Deep breathing exercises</li>
                            <li>• Quick walks outside</li>
                            <li>• Listening to favorite music</li>
                            <li>• Stretching breaks</li>
                        </ul>
                    </div>
                </div>
            </div>
            
            <div class="bg-white border border-green-200 rounded-lg p-4">
                <h4 class="font-semibold text-green-700 mb-3">📅 Weekly Self-Care (30-60 menit)</h4>
                <div class="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                        <h5 class="font-medium">Physical Activities</h5>
                        <ul class="space-y-1">
                            <li>• Yoga or exercise class</li>
                            <li>• Nature walks/hiking</li>
                            <li>• Swimming or cycling</li>
                            <li>• Dance or sports</li>
                        </ul>
                    </div>
                    <div>
                        <h5 class="font-medium">Mental Restoration</h5>
                        <ul class="space-y-1">
                            <li>• Reading for pleasure</li>
                            <li>• Creative hobbies</li>
                            <li>• Social time with friends</li>
                            <li>• Professional development</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
        
        <h3 class="font-bold mt-6 mb-3 text-lg">🆘 Recognizing Burnout Signs</h3>
        <div class="grid md:grid-cols-2 gap-6 text-sm">
            <div class="bg-white border border-red-200 rounded-lg p-4">
                <h5 class="font-semibold text-red-700 mb-2">Physical Symptoms</h5>
                <ul class="space-y-1">
                    <li>• Chronic fatigue dan exhaustion</li>
                    <li>• Frequent headaches atau muscle pain</li>
                    <li>• Changes in sleep patterns</li>
                    <li>• Weakened immune system</li>
                </ul>
            </div>
            
            <div class="bg-white border border-orange-200 rounded-lg p-4">
                <h5 class="font-semibold text-orange-700 mb-2">Emotional Signs</h5>
                <ul class="space-y-1">
                    <li>• Feelings of helplessness</li>
                    <li>• Decreased satisfaction</li>
                    <li>• Loss of motivation</li>
                    <li>• Cynical outlook</li>
                </ul>
            </div>
            
            <div class="bg-white border border-yellow-200 rounded-lg p-4">
                <h5 class="font-semibold text-yellow-700 mb-2">Behavioral Changes</h5>
                <ul class="space-y-1">
                    <li>• Withdrawal from responsibilities</li>
                    <li>• Isolation from others</li>
                    <li>• Procrastination</li>
                    <li>• Using food, drugs, or alcohol to cope</li>
                </ul>
            </div>
            
            <div class="bg-white border border-purple-200 rounded-lg p-4">
                <h5 class="font-semibold text-purple-700 mb-2">Cognitive Effects</h5>
                <ul class="space-y-1">
                    <li>• Lack of creativity</li>
                    <li>• Difficulty concentrating</li>
                    <li>• Negative self-talk</li>
                    <li>• Memory problems</li>
                </ul>
            </div>
        </div>
        
        <div class="bg-green-50 p-4 rounded-lg mt-6">
            <h4 class="font-bold text-green-800 mb-2">💪 Building Resilience</h4>
            <div class="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                    <h5 class="font-medium text-green-700">Mindset Shifts</h5>
                    <ul class="text-green-600 space-y-1">
                        <li>• Practice self-compassion</li>
                        <li>• Embrace imperfection</li>
                        <li>• Focus on progress, not perfection</li>
                        <li>• Celebrate small wins</li>
                    </ul>
                </div>
                <div>
                    <h5 class="font-medium text-green-700">Support Systems</h5>
                    <ul class="text-green-600 space-y-1">
                        <li>• Build your village</li>
                        <li>• Seek professional help when needed</li>
                        <li>• Join parent support groups</li>
                        <li>• Delegate and ask for help</li>
                    </ul>
                </div>
            </div>
        </div>
        
        <div class="bg-blue-50 p-4 rounded-lg mt-6">
            <h4 class="font-bold text-blue-800 mb-2">📚 Resources & Support</h4>
            <ul class="text-blue-700 text-sm space-y-1">
                <li>• Parent mental health hotlines</li>
                <li>• Online therapy platforms</li>
                <li>• Local parenting groups</li>
                <li>• Workplace employee assistance programs</li>
                <li>• Pediatrician referrals for parent support</li>
            </ul>
        </div>
    `
}
];
